// MobileBlockReorderSheet — mobile-only BottomSheet for reordering wish
// blocks via long-press + up/down chevrons (per `DESIGN.md §18.7` +
// `loop-goals/W34.2-COMPREHENSIVE-PROMPT.md` outcome W34.2.3).
//
// Activation flow (mobile-only):
//   1. User taps a block row in LayoutEditor (long-press 250ms).
//   2. The handler calls `navigator.vibrate?.(12)` if available (Safari
//      ignores silently).
//   3. `MobileBlockReorderSheet` opens at `BottomSheet variant="mobile"`
//      large detent.
//   4. Up / down chevron buttons (44pt+ targets per Apple HIG §15) move
//      the block within `order` (the local `useState` holding the new
//      sequence; it is seeded from `blocks` sorted by `position`).
//   5. "完成" closes the sheet + calls `onCommit(newOrder)` which the
//      parent uses to PATCH `/blocks/reorder` with a gap-based renumber
//      starting at 1,000,000 (per DATA_MODEL §8.4).
//
// Desktop: not rendered (the parent LayoutEditor early-returns the
// mobile branch and uses dnd-kit's `SortableContext` instead).

import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import type { WishBlockOut } from "@/lib/api/wishlist";
import { cn } from "@/lib/utils/cn";

export interface MobileBlockReorderSheetProps {
  blocks: WishBlockOut[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new block-id order when the user taps 「完成」. */
  onCommit: (orderedIds: string[]) => void | Promise<void>;
}

// W35.5: `useLongPress` moved to `./useBlockLongPress` so this file exports
// components only (react-refresh /only-export-components).

export function MobileBlockReorderSheet({
  blocks,
  open,
  onOpenChange,
  onCommit,
}: MobileBlockReorderSheetProps) {
  const [order, setOrder] = useState<string[]>(() =>
    [...blocks].sort((a, b) => a.position - b.position).map((b) => b.id),
  );

  // Resync when the upstream blocks change (e.g. parent refetch).
  useEffect(() => {
    setOrder(
      [...blocks].sort((a, b) => a.position - b.position).map((b) => b.id),
    );
  }, [blocks]);

  const move = (id: string, dir: -1 | 1) => {
    setOrder((prev) => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      const moved: string | undefined = arr.splice(idx, 1)[0];
      if (typeof moved === "string") {
        arr.splice(next, 0, moved);
      }
      return arr;
    });
  };

  const byId = new Map<string, WishBlockOut>(
    blocks.map((b): [string, WishBlockOut] => [b.id, b]),
  );
  const ordered = order
    .map((id) => byId.get(id))
    .filter((b): b is WishBlockOut => Boolean(b));

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="mobile"
      title="調整區塊順序"
      description="使用上下箭頭調整，完成後按「完成」送出"
      className="max-h-[80vh] overflow-hidden flex flex-col"
    >
      <div
        data-component="MobileBlockReorderSheet"
        data-testid="mobile-block-reorder-sheet"
        className="flex flex-col gap-s-3 -mx-5 -mb-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]"
      >
        <ul
          data-testid="mobile-block-reorder-list"
          className="flex flex-col gap-s-1 px-s-5"
        >
          {ordered.map((block, idx) => (
            <li
              key={block.id}
              data-testid={`mobile-reorder-row-${block.id}`}
              data-block-id={block.id}
              data-position={idx}
              className="flex items-center gap-2 px-s-3 py-s-2 rounded-2xl border border-hairline bg-paper-elevated"
            >
              <GripVertical
                className="h-4 w-4 text-ink-3 shrink-0"
                aria-hidden
              />
              <span className="text-meta text-ink-3 tabular-nums w-6 text-center shrink-0">
                {idx + 1}
              </span>
              <span className="flex-1 min-w-0 text-body-sm text-ink-1 truncate">
                {block.title || block.block_type}
              </span>
              <button
                type="button"
                aria-label="上移"
                onClick={() => move(block.id, -1)}
                disabled={idx === 0}
                data-testid={`mobile-reorder-up-${block.id}`}
                className={cn(
                  "press min-h-11 min-w-11 flex items-center justify-center rounded-full text-ink-1 hover:bg-paper-calm disabled:opacity-30",
                )}
              >
                <ChevronUp className="h-5 w-5" aria-hidden />
              </button>
              <button
                type="button"
                aria-label="下移"
                onClick={() => move(block.id, 1)}
                disabled={idx === ordered.length - 1}
                data-testid={`mobile-reorder-down-${block.id}`}
                className={cn(
                  "press min-h-11 min-w-11 flex items-center justify-center rounded-full text-ink-1 hover:bg-paper-calm disabled:opacity-30",
                )}
              >
                <ChevronDown className="h-5 w-5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-s-2 px-s-5 pt-s-2 border-t border-hairline">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="mobile-reorder-cancel"
            className="press min-h-12 flex-1 rounded-full border border-hairline bg-paper-elevated text-ink-1 text-body-sm font-semibold"
          >
            取消
          </button>
          <button
            type="button"
            onClick={async () => {
              await onCommit(order);
              onOpenChange(false);
            }}
            data-testid="mobile-reorder-confirm"
            className="press min-h-12 flex-1 rounded-full bg-ink-1 text-paper text-body-sm font-semibold"
          >
            完成
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}